/**
 * StorybookPremiumSidebar — weekly journey, interview mode, chapter draft, export.
 */
import React, { useState } from 'react';
import {
  Mic, CalendarClock, Sparkles, Eye, Wand2, ChevronRight, FileText, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { storybookApi, REMINDER_FREQUENCY_OPTIONS } from '../../utils/storybook';
import { getApiBase } from '../../utils/apiBase';
import axios from 'axios';

const API = getApiBase();

export function StorybookPremiumSidebar({
  storybookId,
  storybook,
  activeChapterId,
  chapterCompletedCount = 0,
  journeySettings = {},
  nextSuggestedPrompt,
  readiness,
  onJourneyUpdated,
  onGoToPrompt,
  onStartInterview,
}) {
  const [savingJourney, setSavingJourney] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [chapterDraft, setChapterDraft] = useState(null);

  const handleJourneyChange = async (field, value) => {
    setSavingJourney(true);
    try {
      const updated = await storybookApi.updateJourney(storybookId, { [field]: value });
      onJourneyUpdated?.(updated.journey_settings);
      if (field === 'reminder_frequency' && value !== 'off') {
        toast.message('Preference saved', {
          description: 'Weekly reminders are not sent automatically yet — your choice is saved for when this feature launches.',
          duration: 5000,
        });
      }
    } catch {
      toast.error('Could not save journey settings.');
    } finally {
      setSavingJourney(false);
    }
  };

  const handlePreviewLegacyBook = async () => {
    const treeId = storybook?.tree_id;
    if (!treeId) return;
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/trees/${treeId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Opening Legacy Book PDF from your archive — review draft available in archive settings.');
    } catch {
      toast.error('Could not generate preview. Complete a few answers first.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleChapterDraft = async () => {
    if (!activeChapterId) return;
    if (chapterCompletedCount < 3) {
      toast.message('Add at least 3 completed answers in this chapter to create a chapter draft.');
      return;
    }
    setDraftLoading(true);
    try {
      const data = await storybookApi.createChapterDraft(storybookId, activeChapterId);
      setChapterDraft(data.chapter_draft);
      toast.success('Chapter draft created — review below.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create chapter draft.');
    } finally {
      setDraftLoading(false);
    }
  };

  const canDraft = chapterCompletedCount >= 3;

  return (
    <aside className="space-y-5 lg:w-72 shrink-0" data-testid="storybook-premium-sidebar">
      <section className="rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)] px-4 py-4">
        <div className="mb-3 flex items-center gap-2">
          <Mic className="h-4 w-4 text-[#8b7355]/80" aria-hidden />
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
            Interview Mode
          </p>
        </div>
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground/75">
          One question at a time — ideal for interviewing an older relative. Record, transcribe, and move on.
        </p>
        <Button
          type="button"
          size="sm"
          className="w-full rounded-full bg-[#8B7355] hover:bg-[#7A6548] text-white"
          onClick={onStartInterview}
          data-testid="storybook-start-interview"
        >
          Start interview
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </section>

      <section className="rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)] px-4 py-4">
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[#8b7355]/80" aria-hidden />
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
            Weekly guided journey
          </p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              id="weekly-prompts"
              type="checkbox"
              checked={Boolean(journeySettings.weekly_prompts_enabled)}
              disabled={savingJourney}
              onChange={(e) => handleJourneyChange('weekly_prompts_enabled', e.target.checked)}
              className="h-4 w-4 rounded border-[#e8e2d8]"
            />
            <Label htmlFor="weekly-prompts" className="text-xs cursor-pointer font-normal">
              Enable weekly prompts
            </Label>
          </div>
          <select
            value={journeySettings.reminder_frequency || 'off'}
            disabled={savingJourney}
            onChange={(e) => handleJourneyChange('reminder_frequency', e.target.value)}
            className="w-full rounded-lg border border-[hsl(38_14%_86%)] bg-white px-2 py-1.5 text-xs"
          >
            {REMINDER_FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {nextSuggestedPrompt && (
          <div className="mt-4 rounded-lg border border-dashed border-[hsl(38_14%_86%)] bg-muted/20 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
              Next suggested prompt
            </p>
            <p className="mt-1 font-serif text-xs leading-relaxed line-clamp-3">&ldquo;{nextSuggestedPrompt.prompt_text}&rdquo;</p>
            <Button type="button" variant="link" size="sm" className="mt-1 h-auto p-0 text-xs" onClick={() => onGoToPrompt?.(nextSuggestedPrompt)}>
              Continue this prompt
            </Button>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_98%)]/80 px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2C4A3B]/8">
            <Wand2 className="h-4 w-4 text-[#2C4A3B]/80" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground/90">Create chapter draft</p>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/75">
              Turn completed answers in this chapter into a draft chapter. AI only organises what you wrote or recorded — review and edit before it is final.
            </p>
            <p className="mt-1 text-[10px] tabular-nums text-muted-foreground">
              {chapterCompletedCount} completed in this chapter
              {canDraft ? '' : ' · need 3+ for draft'}
            </p>
            <Button
              type="button"
              size="sm"
              variant={canDraft ? 'default' : 'outline'}
              className="mt-2 h-8 rounded-full text-xs"
              disabled={!canDraft || draftLoading}
              onClick={handleChapterDraft}
            >
              {draftLoading ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Creating…
                </>
              ) : (
                'Create chapter draft'
              )}
            </Button>
            {!canDraft && (
              <p className="mt-2 text-[10px] text-muted-foreground italic">
                Add at least 3 completed answers to create a chapter draft.
              </p>
            )}
            {chapterDraft ? (
              <div className="mt-3 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800/80">
                  Draft — review before export
                </p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200/50 bg-amber-50/30 p-2 text-[11px] leading-relaxed font-serif whitespace-pre-wrap">
                  {chapterDraft.slice(0, 1200)}
                  {chapterDraft.length > 1200 ? '…' : ''}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_98%)]/80 px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2C4A3B]/8">
            <FileText className="h-4 w-4 text-[#2C4A3B]/80" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground/90">Legacy Book connection</p>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground/75">
              Completed answers can be reviewed and included in your Legacy Book PDF. Chapter drafts stay in review until you approve them.
            </p>
            <div className="mt-2 flex flex-col gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 rounded-full text-xs justify-start"
                disabled={previewLoading}
                onClick={handlePreviewLegacyBook}
              >
                {previewLoading ? 'Generating…' : 'Preview Legacy Book'}
              </Button>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Included in Legacy Book export when completed
              </p>
            </div>
          </div>
        </div>
      </section>
    </aside>
  );
}

export default StorybookPremiumSidebar;
